import "common/osano.d.ts";

export type AnalyticsConsentStatus = "granted" | "denied";

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Max time to wait for Osano script to load
const OSANO_LOAD_TIMEOUT_MS = 10000;

/**
 * Wait for Osano to load.
 * Returns null if Osano doesn't load within the timeout.
 */
const waitForOsano = async (): Promise<NonNullable<
  typeof window.Osano
> | null> => {
  const startTime = Date.now();

  while (!window.Osano?.cm) {
    if (Date.now() - startTime > OSANO_LOAD_TIMEOUT_MS) {
      return null;
    }
    await sleep(100);
  }

  return window.Osano;
};

/**
 * Wait for initial analytics consent decision from Osano.
 * Returns "granted" if user accepts, "denied" if user denies or Osano fails to load.
 */
export const waitForAnalyticsConsent =
  async (): Promise<AnalyticsConsentStatus> => {
    const osano = await waitForOsano();
    if (!osano) {
      return "denied"; // Osano never loaded, default to denied
    }

    // Check if already decided
    const consent = osano.cm.getConsent();
    if (consent.ANALYTICS === "ACCEPT") {
      return "granted";
    }
    if (consent.ANALYTICS === "DENY") {
      return "denied";
    }

    // Wait for user decision (pending state)
    return new Promise((resolve) => {
      // osano-cm-analytics fires when ANALYTICS = ACCEPT
      osano.cm.addEventListener("osano-cm-analytics", () => {
        resolve("granted");
      });

      // For denial, listen to consent-changed and check
      osano.cm.addEventListener("osano-cm-consent-changed", () => {
        const newConsent = osano.cm.getConsent();
        if (newConsent.ANALYTICS === "DENY") {
          resolve("denied");
        }
      });
    });
  };

/**
 * Subscribe to analytics consent changes (for handling revocation).
 * Callback is called when consent status changes after initial decision.
 */
export const onAnalyticsConsentChange = (
  callback: (status: AnalyticsConsentStatus) => void,
): void => {
  const osano = window.Osano;
  if (!osano?.cm) return;

  osano.cm.addEventListener("osano-cm-consent-changed", () => {
    const consent = osano.cm.getConsent();
    if (consent.ANALYTICS === "ACCEPT") {
      callback("granted");
    } else if (consent.ANALYTICS === "DENY") {
      callback("denied");
    }
  });
};
